import { useEffect, useState } from "react";
import {
  deleteTenantDepartmentData,
  getTenantDepartmentsData,
  putTenantDepartmentsData,
} from "@services/API/Requests/TenantDepartmentRequests";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { DEPARTMENT } from "@types/department";
import { error_message } from "@constants/Errors";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { KAKELY_PATHS } from "@constants/NavigationPaths";
import { ConvertAndDownloadCSV } from "@services/CsvDownloadHandler";
import { getTenantDepartmentCsv } from "@services/API/Requests/TenantDepartmentRequests";

export const useEditDepartmentGroup = () => {
  const navigate = useNavigate();
  const [originalTenantDepartments, setOriginalTenantDepartments] = useState<
    DEPARTMENT[]
  >([]);
  const [tenantDepartments, setTenantDepartments] = useState<DEPARTMENT[]>([]);
  const [selectedDepartments, setSelectedDepartments] =
    useState<DEPARTMENT | null>([]);
  const [expandedNodes, setExpandedNodes] = useState<{
    [key: string]: boolean;
  }>({});

  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalTenantDepartments, setTotalTenantDepartments] =
    useState<number>(0);

  const [itemsPerPage, setItemsPerPage] = useState<number>(5);

  const totalPages: number = Math.ceil(totalTenantDepartments / itemsPerPage);

  const errorToast = (response) => toast.error(`${response}`);
  const successToast = (response) => toast.success(`${response}`);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const warningModalTitle: string = "部署を削除";
  const warningModalBody: string =
    "部署を削除します。\nこの操作により、関連するすべての子部署も削除されます。\nよろしいですか？";
  const warningModalConfirm: string = "削除";
  const filename: string = "名分の部署情報_";

  const [parentModalOpen, setParentModalOpen] = useState<boolean>(false);

  const [selectedNodeId, setSelectedNodeId] = useState<number>(0);

  const updateErrorToast = () => toast.error(`${error}`);

  const toggleExpand = (id) => {
    setExpandedNodes((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const fetchTenantDepartmentData = async (curPage = currentPage) => {
    try {
      setLoading(true);
      await getTenantDepartmentsData(
        setTenantDepartments,
        setOriginalTenantDepartments,
        setLoading,
        setError,
        setTotalTenantDepartments,
        curPage,
        itemsPerPage,
      );
    } catch (err) {
      setError(error_message.departments.load_failed);
      updateErrorToast;
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantDepartmentData();
  }, []);

  const handleOpenEditPage = (index) => {
    navigate(`/KAKELY/departments/settings/${index}`);
  };

  const handleOpenCreatePage = (index) => {
    navigate(`/KAKELY/departments/settings/create/${index}`);
  };

  const handleOpenChangeParentModal = (nodeId) => {
    setSelectedNodeId(nodeId);
    setParentModalOpen(true);
  };

  const handleCloseParentModal = () => {
    setParentModalOpen(false);
  };

  const handleConfirmParentModal = (id, newParent) => {
    setParentModalOpen(false);

    const departmentIndex: number = tenantDepartments.findIndex(
      (dept) => dept.id === id,
    );

    if (departmentIndex !== -1) {
      const updatedDepartments = [...tenantDepartments];

      updatedDepartments[departmentIndex] = {
        ...updatedDepartments[departmentIndex],
        parent: newParent,
      };

      setTenantDepartments(updatedDepartments);

      putTenantDepartmentsData(
        updatedDepartments[departmentIndex],
        setLoading,
        setError,
      )
        .then(() => {
          fetchTenantDepartmentData();
        })
        .catch((err) => {
          console.error(error_message.departments.parent_failed, err);
        });
    } else {
      console.error(error_message.departments.not_found);
    }
  };

  const handleDelete = async (event) => {
    setDeleteModalOpen(false);
    event.preventDefault();

    await deleteTenantDepartmentData(selectedNodeId, setLoading, setError)
      .then((response) => {
        if (response.status == 200) {
          successToast(response.message);
          fetchTenantDepartmentData(currentPage);
        } else {
          const errorMessage = TranslateErrorMessage(response.message);
          setError(errorMessage);
          errorToast(errorMessage);
        }
      })
      .catch((error) => {
        const errorMessage = TranslateErrorMessage(error.message);
        setError(errorMessage);
        errorToast(errorMessage);
      });

    onCancel();
  };

  const onCancel = () => {
    handleCloseDeleteModal();
  };

  const handleOpenDeleteModal = (node_id) => {
    setSelectedNodeId(node_id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleDownloadClick = async () => {
    await getTenantDepartmentCsv(
      setLoadingDownload,
      setError,
      selectedDepartments,
    ).then((data) => {
      ConvertAndDownloadCSV(0, data, filename);
    });
  };

  const handlePageChange = (newPage: number) => {
    fetchTenantDepartmentData(newPage);
    setCurrentPage(newPage);
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchTenantDepartmentData(1);
  }, [itemsPerPage]);

  const headerItems: {
    icon: {
      width: number;
      height: number;
      viewBox: string;
      path: string;
    };
    link: string;
    text: string;
    onClick?: () => void;
  }[] = [
    {
      icon: {
        width: 22,
        height: 22,
        viewBox: "0 0 22 22",
        path: "M11 4v14M4 11h14",
      },
      link: "",
      onClick: () => handleOpenCreatePage("Master"),
      text: "最上位階層の追加",
    },
    {
      icon: {
        width: 22,
        height: 22,
        viewBox: "0 0 22 22",
        path: "M6 19.6621C3.01099 17.9331 1 14.7013 1 10.9999C1 5.47709 5.47715 0.999939 11 0.999939C16.5228 0.999939 21 5.47709 21 10.9999C21 14.7014 18.989 17.9331 16 19.6621M15 11L11 7.00001M11 7.00001L7 11M11 7.00001V21",
      },
      link: `${KAKELY_PATHS.csvUploadDepartmentGroup}`,
      text: "CSVアップロード",
    },
    {
      icon: {
        width: 24,
        height: 24,
        viewBox: "0 0 24 24",
        path: "M17 3.33782C19.989 5.06687 22 8.29859 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 8.29859 4.01099 5.06687 7 3.33782M8 12L12 16M12 16L16 12M12 16V2",
      },
      link: "",
      text: "CSVダウンロード",
      onClick: handleDownloadClick,
    },
  ];

  return {
    tenantDepartments,
    originalTenantDepartments,
    expandedNodes,
    loading,
    warningModalTitle,
    warningModalBody,
    warningModalConfirm,
    deleteModalOpen,
    parentModalOpen,
    selectedNodeId,
    headerItems,
    currentPage,
    totalPages,
    itemsPerPage,
    totalTenantDepartments,
    selectedDepartments,
    loadingDownload,
    setLoadingDownload,
    handlePageChange,
    setItemsPerPage,
    setSelectedDepartments,
    toggleExpand,
    handleDelete,
    handleOpenChangeParentModal,
    handleOpenDeleteModal,
    handleOpenCreatePage,
    handleOpenEditPage,
    handleCloseDeleteModal,
    handleCloseParentModal,
    handleConfirmParentModal,
  };
};
